import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import ModulesRoles from './ModulesRoles';
import { getToken } from '../../services/authAPIS';
import { showSnackbar } from '../../components/Snackbar';

const normalizeActions = (actionsRaw) => {
  if (Array.isArray(actionsRaw)) return Array.from(new Set(actionsRaw.map((a) => String(a || '').toLowerCase()).filter(Boolean)));
  if (typeof actionsRaw === 'string') return [actionsRaw.toLowerCase()];
  return [];
};

const normalizeModules = (mods) =>
  (Array.isArray(mods) ? mods : [])
    .map((m) => {
      const name = String(m?.name || '').toLowerCase();
      const actions = normalizeActions(m?.actions);
      return name && actions.length ? { name, actions } : null;
    })
    .filter(Boolean);

export default function EditRoles() {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const initialRole = location.state?.role;
  const hasInitialModules = Array.isArray(initialRole?.modules) && initialRole.modules.length > 0;
  const roleId = params?.id || initialRole?._id;

  const [roleName, setRoleName] = React.useState(initialRole?.name || '');
  const [roleType, setRoleType] = React.useState(initialRole?.roleType || 'other');
  const [isAdmin, setIsAdmin] = React.useState(Boolean(initialRole?.isAdmin || initialRole?.roleType === 'admin'));
  const [modules, setModules] = React.useState(() => normalizeModules(initialRole?.modules));

  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'info' });

  const setSnack = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
    try {
      showSnackbar({ type: severity, message });
    } catch { }
  };

  const applyRoleData = React.useCallback((data) => {
    setRoleName(data?.name || '');
    setRoleType(data?.roleType || 'other');
    setIsAdmin(Boolean(data?.roleType === 'admin'));
    setModules(normalizeModules(data?.modules));
  }, []);

  const loadRole = React.useCallback(() => {
    // If navigation already supplied the role with modules, use it and skip network.
    if (hasInitialModules) {
      setError('');
      applyRoleData(initialRole);
      return undefined;
    }
    if (!roleId) {
      setError('Role id is missing');
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const token = getToken();
        if (!token) throw new Error('Token missing');
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/roleMasters/${roleId}`;
        const res = await axios.get(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        const data = res?.data?.data || res?.data?.role || res?.data;
        if (!cancelled && data) applyRoleData(data);
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Failed to load role';
        if (!cancelled) {
          setError(msg);
          setSnack(msg, 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [applyRoleData, hasInitialModules, initialRole, roleId]);

  React.useEffect(() => {
    // Access control: Only Admin can access Role section
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        const role = (user.role || '').toLowerCase();
        if (role !== 'admin' && role !== 'superadmin') {
          setSnack('Access Denied: You do not have permission to access the Role section.', 'error');
          setTimeout(() => {
            history.push('/app/dashboard');
          }, 1500);
          return;
        }
      }
    } catch (err) {
      console.error('Error checking user role:', err);
    }

    if (!roleId) return undefined;
    const cleanup = loadRole();
    return cleanup;
  }, [loadRole, roleId, history]);

  const handleSave = async () => {
    if (!roleId) {
      setSnack('Role id is missing', 'error');
      return;
    }
    if (!roleName.trim()) {
      setSnack('Role name is required', 'error');
      return;
    }
    if (!modules || modules.length === 0) {
      setSnack('Select at least one module/action', 'error');
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      if (!token) throw new Error('Token missing');
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/roleMasters/${roleId}`;
      const payload = {
        name: roleName.trim(),
        roleType,
        modules,
      };
      await axios.put(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const successMsg = 'Role updated successfully';
      setSnack(successMsg, 'success');
      history.push('/app/creteRoles', { refreshEditRoles: true, message: successMsg });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to update role';
      setSnack(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 1200,
        mx: 'auto',
      }}
    >
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          boxShadow: '0 10px 30px rgba(15,23,42,0.06), 0 0 0 1px rgba(148,163,184,0.2)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Edit Role
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update permissions for this role.
            </Typography>
          </Box>
          <Button onClick={() => history.goBack()} disabled={saving}>
            Back
          </Button>
        </Box>

        <Stack spacing={2} sx={{ mb: 2 }}>
          {error ? (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          ) : null}
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading role details…
              </Typography>
            </Box>
          ) : null}
        </Stack>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Role Type"
              value={roleType}
              onChange={(e) => {
                const val = e.target.value;
                setRoleType(val);
                setIsAdmin(val === 'admin');
              }}
              fullWidth
              // disabled={roleName.toLowerCase() === 'admin'}
              disabled={true}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="subadmin">Sub Admin</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Role Name"
              fullWidth
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              // disabled={isAdmin}
              disabled={true}
              helperText={isAdmin ? 'Admin name cannot be changed' : ''}
              placeholder="e.g. Regional Manager"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Modules & Permissions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Check or uncheck modules, and choose read or write actions.
        </Typography>
        <ModulesRoles value={modules} onChange={setModules} disabled={saving || loading} />

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => loadRole()} disabled={saving || loading}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999, px: 3 }}
          >
            {saving ? (
              <CircularProgress size={18} sx={{ color: 'inherit', mr: 1 }} />
            ) : null}
            Save
          </Button>
        </Box>
      </Paper>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
